import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowRight, Cube, ClipboardText, ListChecks, Buildings, Package, Stack, Warning, Factory } from "@phosphor-icons/react";

const CAT_COLORS = {
  Clamp: "#1d4ed8",
  Rail: "#3b82f6",
  Bracket: "#0ea5e9",
  Strap: "#22c55e",
  Bolt: "#a855f7",
  Nut: "#f97316",
  Spacer: "#06b6d4",
  Pin: "#ef4444",
  Rivet: "#64748b",
  Other: "#94a3b8",
  Rails: "#3b82f6",
  "Clamps-Brackets": "#0ea5e9",
  "Clamps-Straps": "#22c55e",
  Spacers: "#06b6d4",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [grns, setGrns] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [consumables, setConsumables] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, g, b, a, c] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/grns"),
        api.get("/boxes"),
        api.get("/dashboard/stock-alerts"),
        api.get("/consumables"),
      ]);
      setStats(s.data);
      setGrns(g.data.slice(0, 5));
      setBoxes(b.data.slice(0, 5));
      setAlerts(a.data.alerts || []);
      setConsumables(c.data || []);
    })();
  }, []);

  if (!stats) return <div className="font-display uppercase">Loading dashboard...</div>;

  const KPIS = [
    { label: "GRNs", value: stats.grns_count, icon: ClipboardText, link: "/grns" },
    { label: "Productions", value: stats.productions_count || 0, icon: ClipboardText, link: "/productions" },
    { label: "Boxes Total", value: stats.boxes_count, icon: Cube, link: "/boxes" },
    { label: "Boxes Today", value: stats.boxes_today, icon: Cube },
    { label: "Pending Boxes", value: stats.boxes_pending, icon: Cube, tag: "Not packed" },
    { label: "Packing Lists", value: stats.pl_count, icon: ListChecks, link: "/packing-lists" },
    { label: "Suppliers", value: stats.suppliers_count, icon: Buildings, link: "/suppliers" },
    { label: "Parts", value: stats.parts_count, icon: Package, link: "/parts" },
  ];

  const invEntries = Object.entries(stats.inventory_by_category);
  const invTotal = invEntries.reduce((acc, [, v]) => acc + v, 0) || 1;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {KPIS.map((k) => {
          const Icon = k.icon;
          const card = (
            <div className="brut-card p-4 h-full flex flex-col justify-between min-h-[110px] hover:translate-y-[-2px] hover:brut-shadow-lg transition-all">
              <div className="flex items-start justify-between">
                <div className="brut-label">{k.label}</div>
                <Icon size={18} weight="bold" />
              </div>
              <div>
                <div className="font-display text-4xl tracking-tighter">{k.value}</div>
                {k.tag && <div className="brut-tag brut-tag-yellow mt-1 inline-block">{k.tag}</div>}
              </div>
            </div>
          );
          return k.link ? (
            <Link key={k.label} to={k.link} data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>{card}</Link>
          ) : (
            <div key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>{card}</div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stock Alerts (sub-assemblies) */}
        <div className={`brut-card p-6 ${alerts.length > 0 ? "ring-4 ring-red-400" : ""}`} data-testid="stock-alerts-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl uppercase tracking-tight flex items-center gap-2">
              <Warning size={18} weight="bold" /> Sub-Assembly Alerts
            </h2>
            <span className={`brut-tag ${alerts.length > 0 ? "brut-tag-red" : "brut-tag-green"}`}>{alerts.length}</span>
          </div>
          {alerts.length === 0 ? (
            <div className="text-xs text-slate-500">All sub-assembly stock is above thresholds.</div>
          ) : (
            <ul className="space-y-2" data-testid="stock-alerts-list">
              {alerts.map((a) => (
                <li key={a.part_id} className="flex items-center justify-between border-b border-slate-300 pb-2" data-testid={`alert-${a.part_number}`}>
                  <div>
                    <div className="font-mono-ui text-sm font-bold">{a.part_number}</div>
                    <div className="text-xs text-slate-600 truncate max-w-[200px]">{a.part_name}</div>
                    <div className="text-[10px] mt-1">
                      <span className="brut-tag brut-tag-red">On-hand: {a.on_hand}</span>
                      <span className="brut-tag brut-tag-white ml-1">Min: {a.threshold}</span>
                    </div>
                  </div>
                  <Link to="/assembly-runs" className="brut-btn brut-btn-primary px-3 py-1 text-[10px] inline-flex items-center gap-1" data-testid={`run-now-${a.part_number}`}>
                    <Factory size={10} weight="bold" /> Run
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Components in Stock (raw parts from GRNs) */}
        <InventoryPanel
          testid="components-panel"
          title="Components in Stock"
          subtitle="Raw parts received from suppliers"
          rows={stats.components_rollup || []}
        />

        {/* Sub-Assemblies in Stock */}
        <InventoryPanel
          testid="subassemblies-panel"
          title="Sub-Assemblies in Stock"
          subtitle="Produced via Assembly Runs"
          rows={stats.subassembly_rollup || []}
        />
      </div>

      {/* Ready Boxes (finished assembled goods awaiting packing list) */}
      <div className="brut-card p-6" data-testid="ready-boxes-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-display text-xl uppercase tracking-tight">Assembled Goods — Boxes Ready to Pack</h2>
            <p className="text-xs text-slate-600 mt-1">Shippable boxes coming out of Assembly Runs · not yet in a Packing List</p>
          </div>
          <Link to="/boxes" className="text-xs font-display uppercase tracking-widest underline">All →</Link>
        </div>
        {(stats.boxes_ready_by_product || []).length === 0 ? (
          <div className="text-xs text-slate-500">No boxes are awaiting packing.</div>
        ) : (
          <table className="brut-table" data-testid="ready-boxes-table">
            <thead><tr><th>Product</th><th>Customer</th><th>Boxes</th><th>Total Units</th></tr></thead>
            <tbody>
              {stats.boxes_ready_by_product.map((b) => (
                <tr key={b.product_part_id} data-testid={`ready-${b.product_part_number}`}>
                  <td>
                    <div className="font-mono-ui text-xs font-bold">{b.product_part_number}</div>
                    <div className="text-xs text-slate-600">{b.product_name}</div>
                  </td>
                  <td className="text-xs">{b.customer || "—"}</td>
                  <td className="font-mono-ui font-bold">{b.boxes}</td>
                  <td className="font-mono-ui">{b.units}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Consumables — manual weekly stock */}
      <div className="brut-card p-6" data-testid="dashboard-consumables">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl uppercase tracking-tight">Consumables Stock</h2>
          <Link to="/parts?type=Consumable" className="brut-tag brut-tag-white text-xs">Manage →</Link>
        </div>
        {consumables.length === 0 ? (
          <div className="text-center py-6 text-zinc-500 text-sm">
            No consumables yet. Add pallets, corrugated, straps etc. as consumable parts.
          </div>
        ) : (
          <table className="brut-table" data-testid="consumables-table">
            <thead><tr><th>Part</th><th>Category</th><th className="text-right">Stock on Hand</th><th>Last Updated</th><th>By</th></tr></thead>
            <tbody>
              {consumables.map((c) => (
                <tr key={c.id} data-testid={`consumable-row-${c.part_number}`}>
                  <td>
                    <div className="font-bold font-mono-ui text-xs">{c.part_number}</div>
                    <div className="text-xs text-zinc-600">{c.name}</div>
                  </td>
                  <td><span className="brut-tag brut-tag-yellow">{c.category}</span></td>
                  <td className="text-right font-mono-ui font-bold">
                    {c.stock_on_hand ?? "—"} <span className="text-xs text-zinc-500">{c.unit || "pcs"}</span>
                  </td>
                  <td className="font-mono-ui text-xs">
                    {c.stock_updated_at ? new Date(c.stock_updated_at).toLocaleDateString() : <span className="text-zinc-500">never</span>}
                  </td>
                  <td className="text-xs">{c.stock_updated_by_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent GRNs */}
        <div className="brut-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl uppercase tracking-tight">Recent GRNs</h2>
            <Link to="/grns" className="text-xs font-display uppercase tracking-widest underline">All →</Link>
          </div>
          {grns.length === 0 ? (
            <div className="text-xs text-zinc-500">No GRNs yet.</div>
          ) : (
            <ul className="space-y-2">
              {grns.map((g) => (
                <li key={g.id} className="flex items-center justify-between border-b border-zinc-300 pb-2">
                  <div>
                    <div className="font-mono-ui text-sm font-bold">{g.grn_number}</div>
                    <div className="text-xs text-zinc-600">{g.supplier_name} · Inv {g.invoice_number}</div>
                  </div>
                  <Link to={`/grns/${g.id}`} className="brut-tag brut-tag-white inline-flex items-center gap-1">
                    View <ArrowRight size={10} weight="bold" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent Boxes */}
        <div className="brut-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl uppercase tracking-tight">Recent Boxes</h2>
            <Link to="/boxes" className="text-xs font-display uppercase tracking-widest underline">All →</Link>
          </div>
          {boxes.length === 0 ? (
            <div className="text-xs text-zinc-500">No boxes assembled yet.</div>
          ) : (
            <ul className="space-y-2">
              {boxes.map((b) => (
                <li key={b.id} className="flex items-center justify-between border-b border-zinc-300 pb-2">
                  <div>
                    <div className="font-mono-ui text-sm font-bold">{b.box_number}</div>
                    <div className="text-xs text-zinc-600">{b.product_name} · {b.units_count} units</div>
                  </div>
                  <Link to={`/boxes/${b.id}`} className="brut-tag brut-tag-yellow inline-flex items-center gap-1">
                    Open <ArrowRight size={10} weight="bold" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function InventoryPanel({ title, subtitle, rows, testid }) {
  const max = rows.reduce((a, r) => Math.max(a, r.qty_remaining || 0), 0) || 1;
  return (
    <div className="brut-card p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display text-xl uppercase tracking-tight flex items-center gap-2">
            <Stack size={18} weight="bold" /> {title}
          </h2>
          <p className="text-[10px] text-slate-600">{subtitle}</p>
        </div>
        <span className="brut-tag brut-tag-white">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500">No stock in this bucket.</div>
      ) : (
        <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {rows.slice(0, 12).map((r) => (
            <li key={r.part_id} className="text-xs" data-testid={`${testid}-row-${r.part_number}`}>
              <div className="flex justify-between items-baseline">
                <div className="min-w-0">
                  <span className="font-bold font-mono-ui">{r.part_number}</span>
                  {r.customer && <span className="brut-tag brut-tag-white ml-2">{r.customer}</span>}
                  <div className="text-[10px] text-slate-600 truncate">{r.part_name}</div>
                </div>
                <span className="font-mono-ui font-bold whitespace-nowrap ml-2">{r.qty_remaining} {r.unit}</span>
              </div>
              <div className="h-1.5 brut-border bg-white mt-1">
                <div className="h-full bg-[#1d4ed8]" style={{ width: `${Math.min(100, (r.qty_remaining / max) * 100)}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

